import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, AlertCircle, CheckCircle } from "lucide-react";
import { notificationService } from "@/services/notificationService";
import { toast } from "@/hooks/use-toast";

interface SendWelcomeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  employeeName: string;
  employeeEmail: string;
}

export const SendWelcomeEmailModal: React.FC<SendWelcomeEmailModalProps> = ({
  isOpen,
  onClose,
  userId,
  employeeName,
  employeeEmail,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await notificationService.sendWelcomeEmail(userId);

      if (response.success) {
        toast({
          title: "Success",
          description: `Welcome email sent successfully to ${employeeName}`,
          variant: "success",
        });
        onClose();
      } else {
        throw new Error(response.message || "Failed to send welcome email");
      }
    } catch (error) {
      console.error("Error sending welcome email:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to send welcome email";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-8 w-8 text-muted-foreground" />
          </div>
          <DialogTitle className="text-xl text-center">
            Send Welcome Email
          </DialogTitle>
          <DialogDescription className="text-center">
            Are you sure you want to send a welcome email to {employeeName}?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
            <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-medium mb-2">Before sending:</p>
              <p>
                Please ensure the employee's email address ({employeeEmail}) is
                correct and they should receive a welcome email at this time.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <div className="flex justify-center gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Welcome Email
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
